<template>
  <main class="staff-page">
    <header class="page-heading">
      <div>
        <div class="page-eyebrow">Аккаунт</div>
        <h1>Мой пароль</h1>
      </div>
    </header>
    <Card class="password-card max-w-xl">
      <CardHeader>
        <CardTitle>Одноразовый пароль</CardTitle>
        <p class="text-sm text-muted-foreground">Действует в течение 24 часов</p>
      </CardHeader>
      <CardContent>
        <p v-if="error" role="alert" class="ui-alert ui-alert--error mb-4">{{ error }}</p>
        <div v-if="loading" class="ui-empty">
          <Spinner />
          <span>Загрузка пароля…</span>
        </div>
        <div v-else-if="password" class="password-box">
          <code class="password-text">{{ password }}</code>
          <Button
            variant="outline"
            size="icon"
            class="copy-icon-btn"
            aria-label="Копировать пароль"
            title="Копировать пароль"
            @click="copyPassword"
          >
            <Copy :size="18" />
          </Button>
        </div>
      </CardContent>
      <CardFooter class="justify-end">
        <Button variant="outline" :disabled="loading" @click="fetchPassword">
          <RefreshCw :size="16" />
          Обновить пароль
        </Button>
      </CardFooter>
    </Card>
    <div v-if="showCopyNotification" class="staff-toast" role="status">
      <Check :size="17" />
      Пароль скопирован в буфер обмена
      <Button
        variant="ghost"
        size="icon"
        aria-label="Закрыть уведомление"
        @click="showCopyNotification = false"
      >
        <X :size="16" />
      </Button>
    </div>
  </main>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Copy, RefreshCw, Check, X } from '@lucide/vue';

import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import axios from 'axios';

const password = ref<string | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const showCopyNotification = ref(false);
let notificationTimer: ReturnType<typeof setTimeout> | null = null;
watch(showCopyNotification, (show) => {
  if (notificationTimer) clearTimeout(notificationTimer);
  if (show)
    notificationTimer = setTimeout(() => {
      showCopyNotification.value = false;
    }, 2000);
});
onBeforeUnmount(() => {
  if (notificationTimer) clearTimeout(notificationTimer);
});

function copyPassword() {
  if (password.value) {
    navigator.clipboard
      .writeText(password.value)
      .then(() => {
        showCopyNotification.value = true;
      })
      .catch((err) => {
        console.error('Не удалось скопировать пароль: ', err);
        error.value = 'Не удалось скопировать пароль. Пожалуйста, попробуйте вручную.';
      });
  }
}

async function fetchPassword() {
  loading.value = true;
  error.value = null;

  try {
    const response = await axios.get('/api/pass/');
    password.value = response.data;
  } catch (err) {
    console.error('Ошибка при получении пароля:', err);
    error.value = 'Не удалось получить пароль. Пожалуйста, попробуйте позже.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchPassword();
});
</script>
